import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {SelfAlignedBounds} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/ComponentDesc';
import {Loader} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/loader/Loader';
import {IViewportInfo} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/viewport/Viewport';
import {IRepeatedData, VisualBox} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/VisualBox';
import {LayoutModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/layouts/LayoutModel';
import {PositioningType} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/layouts/PositioningType';
import {Page} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Page';
import {LoaderModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/loader/LoaderModel';
import {OpacityModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/opacity/OpacityModel';
import {PerspectiveModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/perspective/PerspectiveModel';
import {Repeater} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/repeater/Repeater';
import {RotationModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/rotation/RotationModel';
import {ScaleModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/scale/ScaleModel';
import {SkewModel} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/skew/SkewModel';
import {DnDMask} from '@mintdata-wsp/common/view/surface/DnDMask';
import {BoxViewContent} from '@mintdata-wsp/pub/view/BoxViewContent';
import {IBoxViewState} from '@mintdata-wsp/wsp/ui/views/visualDesigner/surface/area/BoxView';
import {
    BaseComponentWithStateContext,
    componentStateContextTypes,
    IComponentStateContext
} from '@mintdata/mint-kit/api/components/BaseComponentWithStateContext';
import {GUID} from '@mintdata/mint-kit/api/models/GUID';
import {ABoolean} from '@mintdata/mint-kit/api/values/ABoolean';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import {ObjectUtils} from '@mintdata/mint-kit/impl/util/ObjectUtils';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {action, computed} from 'mobx';
import {observer} from 'mobx-react';
import * as React from 'react';
import {DraggableCore} from 'react-draggable';

const styles = require('./BoxView.less');
const cx = classnames.bind(styles);

export interface IBoxViewProps
{
    visualBox:VisualBox;
    repeater:Repeater;
    isInOverlays?:boolean;
    isHiddenOverlay?:boolean,
}

@observer
export class BoxView extends BaseComponentWithStateContext<IBoxViewProps, any>
{
    public static readonly childContextTypes = componentStateContextTypes;

    public static defaultProps:Partial<IBoxViewProps> = {
        isInOverlays: false
    };

    private _uniqueId:GUID = IdUtil.newComponentId();

    @bind
    private _onBoxRef(ref:HTMLElement | null)
    {
        const {visualBox, repeater} = this.props;

        if( ref )
            commonControllers.animation.registerTransformRef(visualBox, repeater, ref);
        else
            commonControllers.animation.unregisterTransformRef(visualBox, repeater);
    }

    private get _isDraggable():boolean
    {
        const {visualBox: {repeatedData}, repeater} = this.props;

        return repeatedData.get(repeater.index).isDraggable;
    }

    private get _isDropTarget():boolean
    {
        const {visualBox, repeater} = this.props;
        const repeatedData:IRepeatedData = visualBox.repeatedData.get(repeater.index);

        return repeatedData.dnd.isEnabled.value && repeatedData.isDropTarget;
    }

    private get _dndGroupName():string
    {
        const {visualBox: {repeatedData}, repeater} = this.props;

        return repeatedData.get(repeater.index).dndGroupName;
    }

    private get _dndTarget():string
    {
        const {visualBox: {repeatedData}, repeater} = this.props;

        return repeatedData.get(repeater.index).dndTarget;
    }

    private get _isVisible():boolean
    {
        const {visualBox: {repeatedData}, repeater} = this.props;

        const visibility:ABoolean = repeatedData.get(repeater.index).visibility;

        return !!(visibility && visibility.value);
    }

    @computed
    private get _isSelfDirected():boolean
    {
        const {visualBox, repeater} = this.props;
        const layout:LayoutModel = visualBox.repeatedData.get(repeater.index).layout;

        return PositioningType.isSelfDirected(layout.positioningType);
    }

    private get _boxRotationStyle():React.CSSProperties | null
    {
        const {visualBox: {repeatedData}, repeater} = this.props;

        const rotation:RotationModel = repeatedData.get(repeater.index).rotation;

        return rotation ? rotation.toCss() : null;
    }

    private get _scale():ScaleModel
    {
        const {visualBox: {repeatedData}, repeater} = this.props;

        return repeatedData.get(repeater.index).scale;
    }

    private get _opacity():OpacityModel
    {
        const {visualBox: {repeatedData}, repeater} = this.props;

        return repeatedData.get(repeater.index).opacity;
    }

    private get _skew():SkewModel
    {
        const {visualBox: {repeatedData}, repeater} = this.props;

        return repeatedData.get(repeater.index).skew;
    }

    private get _perspective():PerspectiveModel
    {
        const {visualBox: {repeatedData}, repeater} = this.props;

        return repeatedData.get(repeater.index).perspective;
    }

    private get _loader():LoaderModel
    {
        const {visualBox: {repeatedData}, repeater} = this.props;

        return repeatedData.get(repeater.index).loader;
    }

    @bind
    private _getBoundsAsCss():React.CSSProperties
    {
        const {visualBox, repeater} = this.props;

        if( this._isSelfDirected )
        {
            const selfAlignedBounds:Map<number, SelfAlignedBounds> = visualBox.selfAlignedBounds;

            return visualBox.box.isViewport
                ? {
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: '100%',
                    height: '100%'
                }
                : {
                    position: 'absolute',
                    ...selfAlignedBounds.get(repeater.index)
                };
        }
        else
        {
            return {
                position: 'relative',
                ...visualBox.repeatedData.get(repeater.index).layoutStyle
            };
        }
    }

    @action
    private _onBoxMouseDown = (event:any) =>
    {
        const {visualBox, repeater} = this.props;
        const {surfaceDnD} = commonControllers;

        if( (event as MouseEvent).button === 0
            || (event.type === 'touchstart'
                && (event as TouchEvent).changedTouches
                && (event as TouchEvent).changedTouches.length === 1) )
        {
            surfaceDnD.processDragStart(visualBox, repeater, this._uniqueId, event);
        }
    };

    private _updateRepeaterCache(props)
    {
        const {repeater} = props;
        const {ui: {visualDesigner: {surfaceEditor: {area: {repeatersCache}}}}} = rootState;

        repeatersCache.set(this._uniqueId, repeater);
    }

    public shouldComponentUpdate(nextProps:IBoxViewProps, nextState:IBoxViewState, nextContext:IComponentStateContext)
    {
        return !ObjectUtils.shallowCompareKeys(this.props, nextProps) ||
            !ObjectUtils.shallowCompareKeys(this.context, nextContext);
    }

    public componentDidMount():void
    {
        this._updateRepeaterCache(this.props);
    }

    public componentWillReceiveProps(newProps):void
    {
        this._updateRepeaterCache(newProps);
    }

    public componentWillUnmount():void
    {
        const {ui: {visualDesigner: {surfaceEditor: {area: {repeatersCache}}}}} = rootState;

        repeatersCache.delete(this._uniqueId);
    }

    private _getViewportInfoProp():IViewportInfo
    {
        const {isInOverlays, visualBox, isHiddenOverlay} = this.props;
        const {surfaceAreaDomElement} = rootState.ui.visualDesigner.surfaceEditor.area;
        const page:Page = visualBox.box.page;
        const pageName = page.name;
        const isActive:boolean = page === commonControllers.designState.getActivePage();

        return visualBox.box.isViewport
            ? {
                isInOverlays,
                closeOverlayFunction: () => pageName && commonControllers.designState.hideOverlayByName(pageName, true),
                pageId: page.id,
                isActive,
                onResize: commonControllers.designState.onViewportResize,
                isTopmost: isInOverlays && commonControllers.designState.isTheTopmostOverlay(page.id),
                surfaceAreaRef: surfaceAreaDomElement,
                isHiddenOverlay,
                isInDesignMode: false
            }
            : null;
    }

    @computed
    private get _cursorStyle():React.CSSProperties | null
    {
        const {surfaceArea} = commonControllers;

        const {visualBox} = this.props;

        const componentName = visualBox.box.component.name;

        if( surfaceArea.isComponentWithSelfCursor(componentName) ) return null;

        const {componentDesc} = visualBox.box;
        const {cursorProp} = componentDesc;

        const isEnabled = cursorProp.value.isEnabled.value;

        return isEnabled ? {cursor: cursorProp.value.toCssString()} : null;
    }

    private _renderContent():JSX.Element
    {
        const {visualBox, repeater, isInOverlays} = this.props;

        //TODO: consider isSSRCompatible here

        const isViewport:boolean = visualBox.box.isViewport;

        const cursorStyle:React.CSSProperties = this._cursorStyle;
		let rotationCss:React.CSSProperties = this._boxRotationStyle || {};

        let style:React.CSSProperties = {
            ...this._getBoundsAsCss(),
            ...rotationCss,
            ...cursorStyle,
            pointerEvents: isViewport && isInOverlays ? 'none' : 'all'
        };

        let scaleCss:React.CSSProperties = this._scale.toCss(style);

        if( scaleCss )
            style = {
                ...style,
                ...scaleCss
            };

        let skewCss:React.CSSProperties = this._skew.toCss(style);

        if( skewCss )
            style = {
                ...style,
                ...skewCss
            };

        let perspectiveCss:React.CSSProperties = this._perspective.toCss(style);

        if( perspectiveCss )
            style = {
                ...style,
                ...perspectiveCss
            };

        if( this._opacity.isEnabled.value && this._opacity.opacity.value !== 1 )
            style.opacity = this._opacity.opacity.value;

        const {area: {surfaceDnDState: {dragContext}}} = rootState.ui.visualDesigner.surfaceEditor;

        return (
            <div
                ref={this._onBoxRef}
                className={cx('boxView', 'contentHolder')}
                style={style}
                data-box-id={visualBox.box.id}
                data-box-unique-id={this._uniqueId}
                data-box-view="true"
                data-box-dnd-group={this._dndGroupName}
                data-box-dnd-target={this._dndTarget}
            >
                <BoxViewContent
                    visualBox={visualBox}
                    repeater={repeater}
                    viewportInfo={this._getViewportInfoProp()}
                />
                <Loader loaderPropValue={this._loader} />
                {
                    dragContext && this._isDropTarget &&
					<DnDMask
						visualBox={visualBox}
						repeater={repeater}
						boxUniqueId={this._uniqueId}
					/>
                }
            </div>
        );
    }

    public render():JSX.Element
    {
        return this._isVisible
            ? (
                this._isDraggable
                    ? (
                        <DraggableCore
                            onMouseDown={this._onBoxMouseDown}
                            enableUserSelectHack={true}
                        >
                            {this._renderContent()}
                        </DraggableCore>
                    )
                    : this._renderContent()
            ) : null;
    }
}
